import { REPORT_CHART_FEEDBACK_BASED_MEASURES } from '@vision/ui/constants';
import { ReportChartFeedbackBaseType } from '@vision/ui/enums';
import { ReportChartConfiguration, ReportChartMeasureType } from '@vision/ui/interfaces';
import { t } from 'i18next';
import { array, lazy, object, string } from 'yup';
import { TableChart1ChartValueComponent, TableChart1CompareRenderer } from '../../components';
import {
  DefaultReportChartCompareSchema,
  DefaultReportChartThemeFont,
  GRAPHIC_TITLE_MAX_CHAR_COUNT,
} from '../default-chart.config';

export const TableChart1DefaultConfiguration: ReportChartConfiguration = {
  validationSchema: object().shape({
    selectedDate: object().shape({
      fromDate: string().required('validation:rules.generic.required'),
      toDate: string().required('validation:rules.generic.required'),
      dateRange: string().optional(),
    }),
    selectedMultipleMeasure: array().of(string()).min(1, 'validation:rules.generic.required'),
    selectedCompare: array()
      .of(string().required('validation:rules.generic.required'))
      .min(1, 'validation:rules.generic.required')
      .max(1, 'validation:rules.generic.max')
      .test('unique', 'validation:rules.generic.unique', (value) => {
        return new Set(value).size === value.length;
      }),
    selectedTheme: object().shape({
      title: object().shape({
        label: string()
          .max(
            GRAPHIC_TITLE_MAX_CHAR_COUNT,
            t('validation:rules.generic.max', {
              count: GRAPHIC_TITLE_MAX_CHAR_COUNT,
            }),
          )
          .required('validation:rules.reports.selectedTheme.title.label.required'),
      }),
    }),
    selectedCompareCriteria: object().shape({
      ...DefaultReportChartCompareSchema,
    }),
    selectedFeedbackBasedQuestionBased: lazy(() =>
      string().when(['selectedMultipleMeasure'], {
        is: (selectedMultipleMeasure: ReportChartMeasureType[]) =>
          (selectedMultipleMeasure ?? []).some((measure) => REPORT_CHART_FEEDBACK_BASED_MEASURES.includes(measure)),
        then: (selectedFeedbackBasedQuestionBased) => selectedFeedbackBasedQuestionBased.required(),
        otherwise: (selectedFeedbackBasedQuestionBased) => selectedFeedbackBasedQuestionBased.notRequired(),
      }),
    ),
    selectedCompareFlowIds: lazy(() =>
      array().when(['selectedFeedbackBasedQuestionBased'], {
        is: (selectedFeedbackBasedQuestionBased: ReportChartFeedbackBaseType) =>
          selectedFeedbackBasedQuestionBased === ReportChartFeedbackBaseType.QUESTION_BASED,
        then: (selectedCompareFlowIds) => selectedCompareFlowIds.required().min(1),
        otherwise: (selectedCompareFlowIds) => selectedCompareFlowIds.notRequired(),
      }),
    ),
    selectedCompareQuestionIds: lazy(() =>
      array().when(['selectedFeedbackBasedQuestionBased'], {
        is: (selectedFeedbackBasedQuestionBased: ReportChartFeedbackBaseType) =>
          selectedFeedbackBasedQuestionBased === ReportChartFeedbackBaseType.QUESTION_BASED,
        then: (selectedCompareQuestionIds) => selectedCompareQuestionIds.required().min(1),
        otherwise: (selectedCompareQuestionIds) => selectedCompareQuestionIds.notRequired(),
      }),
    ),
  }),
  measures: null,
  chartValueComponent: TableChart1ChartValueComponent,
  hideCompare: true,
  compareRendererComponent: TableChart1CompareRenderer,
  defaultFeedbackBasedQuestionBased: ReportChartFeedbackBaseType.FEEDBACK_BASED,
  customize: {
    active: (values) => !!values.selectedMultipleMeasure.length && !!values.selectedCompare.length,
    limits: {
      upperLimitActive: false,
      upperLimit: 0,
      lowerLimitActive: false,
      lowerLimit: 0,
    },
    transactionalDate: {
      active: false,
    },
    feedbackCount: {
      active: false,
    },
  },
  defaultTheme: {
    active: (values) => !!values.selectedMultipleMeasure.length && !!values.selectedCompare.length,
    title: {
      ...DefaultReportChartThemeFont,
      label: '',
    },
    dataLabels: {
      ...DefaultReportChartThemeFont,
      fontBold: false,
      active: false,
      displayFormat: 'auto',
      roundingMethod: 'default',
      position: 'center',
    },
    extraSettings: {
      tableHeader: {
        ...DefaultReportChartThemeFont,
      },
    },
  },
  responseMultiple: true,
};
